import { createApi } from '@reduxjs/toolkit/query/react';
import {
  createBackendBaseQuery,
  transformApiError,
  withCountryHeader,
} from '../../../utils/createAuthBaseQuery';
import { API_URLS } from '../../../constants/apiUrl';

export interface DriveInvestigation {
  id: string;
  dxId: number;
  pc: string;
  databaseModelName: string;
  region: string;
  type: string;
  voltage: number;
  oldDriveRange: string;
  oldDrivePn: string;
  kitNumber: string;
  electronikonOld: string;
  electronikonNew: string;
  newDriveSupplier: string;
  newDrivePn: string;
  status: string;
}

export interface DriveInvestigationSearchResponse {
  totalResult: number;
  activeResult: number;
  driveInvestigations: DriveInvestigation[];
}

export interface FetchDriveInvestigationParams {
  page?: number;
  limit?: number;
  sort?: keyof DriveInvestigation;
  order?: 'asc' | 'desc';
  [key: string]: string | number | undefined;
}

// Mirrors the backend DriveSolutionSearchResultDto. The array fields hold every
// value available for the matched drive, not the machine's own selection.
export interface DriveSolutionResponse {
  oldDrivePartNumber?: number;
  currentDrive?: string;
  brand?: string;
  driveType?: string;
  modelName?: string[];
  productCompany?: string[];
  voltage?: number[];
  frequency?: string[];
  region?: string[];
  elektronikon?: string[];
  motor?: string[];
}

// Mirrors the backend SearchDriveSolutionRequest. Property names bind
// case-insensitively to the request object (e.g. serialNumber -> SerialNumber).
// A search is made by either serialNumber or oldDrivePartNumber.
export interface FetchDriveSolutionParams {
  oldDrivePartNumber?: number;
  serialNumber?: string;
  driveSolutionLogId?: number;
}

export interface DriveBaseKit {
  partNumber: string;
  quantity: number;
  description: string | null;
  driveInKit?: boolean | null;
}

export interface DriveAdditionalComponent {
  partNumber: string;
  quantity: number;
  component: string;
}

// Returned by Solution/Details for the machine configuration picked on the results page.
export interface DriveSolutionDetailsResponse {
  oldDrivePartNumber?: number;
  recommendedSolution?: string;
  kitNumber?: string;
  brand?: string;
  driveType?: string;
  oldElektronikon?: string;
  newElektronikon?: string;
  motor?: string;
  validationDetails?: boolean;
  driveTuning?: boolean;
  documentation?: {
    instructions?: string | null;
    neosDriveQuickstart?: string | null;
    serviceDiagram?: string | null;
    databaseRef?: string | null;
    vsdParameters?: string | null;
  };
  // Free text; may contain line breaks.
  fieldComments?: string | null;
  partsToOrder?: {
    baseKit?: DriveBaseKit | null;
    newDrivePn?: DriveBaseKit | null;
    additionalComponents?: DriveAdditionalComponent[] | null;
  };
}

// The drive and the machine configuration selected in Machine Information.
export interface FetchDriveSolutionDetailsParams {
  oldDrivePartNumber: number;
  modelName: string;
  productCompany: string;
  voltage: string;
  frequency: string;
  region: string;
  elektronikon: string;
  motor: string;
}

const BASE_URL = process.env.REACT_APP_DRIVE_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_DRIVE_SERVICE_API_BASE_URL environment variable is not set');
}

const baseQuery = createBackendBaseQuery(BASE_URL);

// Attach the user's country header to the Drive Solution/Search request;
// the other endpoints in this slice pass through unchanged.
const baseQueryWithCountry = withCountryHeader(
  baseQuery,
  (url) => url.includes(API_URLS.DRIVE_INVESTIGATION.SOLUTION_SEARCH),
  'driveInvestigationApi'
);

export const driveInvestigationApiSlice = createApi({
  reducerPath: 'driveInvestigationApi',
  baseQuery: baseQueryWithCountry,
  tagTypes: ['DriveInvestigation', 'DriveSolution'],
  endpoints(builders) {
    return {
      fetchDriveInvestigations: builders.query<
        DriveInvestigationSearchResponse,
        FetchDriveInvestigationParams | void
      >({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.DRIVE_INVESTIGATION.SEARCH}${queryString}`,
            method: 'GET',
          };
        },
        providesTags: [{ type: 'DriveInvestigation', id: 'LIST' }],
        transformErrorResponse: transformApiError,
      }),
      fetchDriveSolution: builders.query<DriveSolutionResponse, FetchDriveSolutionParams>({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.DRIVE_INVESTIGATION.SOLUTION_SEARCH}${queryString}`,
            method: 'GET',
          };
        },
        providesTags: ['DriveSolution'],
        transformErrorResponse: transformApiError,
      }),
      fetchDriveSolutionDetails: builders.query<
        DriveSolutionDetailsResponse,
        FetchDriveSolutionDetailsParams
      >({
        query: (params) => {
          const usp = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              usp.append(key, String(value));
            }
          });
          return {
            url: `${API_URLS.DRIVE_INVESTIGATION.SOLUTION_DETAILS}?${usp.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['DriveSolution'],
        transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const {
  useFetchDriveInvestigationsQuery,
  useLazyFetchDriveInvestigationsQuery,
  useFetchDriveSolutionQuery,
  useLazyFetchDriveSolutionQuery,
  useFetchDriveSolutionDetailsQuery,
} = driveInvestigationApiSlice;
